/**
 * The "Short job summary" field on Post a Job accepts 80–800 characters. The AI draft has to land
 * inside that window as finished prose: the Cloud Function asks the model to compose within the
 * budget, and both the function and the client run the result through fitTextToLimit() so an
 * over-length reply is cut at the end of a sentence — never mid-word, never mid-sentence.
 *
 * Shared with functions/ via scripts/syncFunctionsGenerated.mjs (functions/src/generated/lib/jobs/).
 */
export const JOB_SUMMARY_MAX_CHARS = 800;

/**
 * The character budget the model is asked to write to. Deliberately under the hard cap so a reply
 * that runs a sentence long still fits without any trimming at all.
 */
export const JOB_SUMMARY_TARGET_CHARS = 700;

/**
 * Return `text` unchanged when it fits in `max` characters; otherwise cut it back to the last
 * complete sentence that fits. If no sentence boundary exists inside the budget, fall back to the
 * last whole word and mark the cut with an ellipsis, so the result still never ends mid-word.
 */
export function fitTextToLimit(text: string, max = JOB_SUMMARY_MAX_CHARS): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;

  const window = trimmed.slice(0, max);

  // Sentence end: terminal punctuation, optionally followed by a closing quote/bracket, and then
  // whitespace or the end of the window. Take the LAST such boundary inside the budget.
  const sentenceEnd = /[.!?]["'”’)\]]*(?=\s|$)/g;
  let lastEnd = -1;
  for (let m = sentenceEnd.exec(window); m; m = sentenceEnd.exec(window)) {
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd > 0) return window.slice(0, lastEnd).trim();

  const lastSpace = window.lastIndexOf(' ');
  const wordCut = lastSpace > 0 ? window.slice(0, lastSpace) : window;
  return `${wordCut.replace(/[\s,;:—–-]+$/, '')}…`;
}
